/**
 * לקוח ה-API לארכיון הצעות (TASKS 5.6) + הצעה ידנית (5.5) + שליחה ידנית
 * בוואטסאפ (5.3).
 */
import type { AdminApiError } from './adminApi.js';

export type QuoteSource = 'site' | 'admin';
export type PriceBasis = 'base' | 'customer' | 'manual';

export interface QuoteListItem {
  id: number;
  quote_number: string;
  customer_id: number;
  customer_full_name: string;
  customer_phone_e164: string;
  source: QuoteSource;
  price_basis: PriceBasis;
  subtotal: string;
  vat_amount: string;
  total: string;
  has_pdf: 0 | 1;
  created_at: string;
}

export interface QuoteDetail {
  id: number;
  quote_number: string;
  customer_id: number;
  source: QuoteSource;
  subtotal: string;
  vat_rate: string;
  vat_amount: string;
  total: string;
  valid_until: string;
  price_basis: PriceBasis;
  has_estimate: number;
  has_price_on_request: number;
  pdf_path: string | null;
  created_at: string;
  customer_full_name: string;
  customer_phone_e164: string;
  customer_email: string | null;
  customer_company: string | null;
}

export interface QuoteItemDetail {
  id: number;
  product_id: number | null;
  variant_id: number | null;
  sku_snapshot: string;
  name_snapshot: string;
  spec_snapshot: unknown;
  qty: string;
  unit_price: string;
  line_total: string;
  price_source: PriceBasis;
  is_estimate: number;
  is_price_on_request: number;
  sort_order: number;
}

export interface EmailDelivery {
  recipient_kind: 'customer' | 'owner';
  to_email: string;
  status: 'queued' | 'sent' | 'failed' | 'pending_retry';
  error_message: string | null;
  attempt_no: number;
  created_at: string;
  updated_at: string;
}

export interface WhatsappDelivery {
  to_phone_e164: string;
  channel: 'service' | 'template';
  status: 'queued' | 'sent' | 'delivered' | 'read' | 'failed' | 'pending_retry';
  error_message: string | null;
  attempt_no: number;
  created_at: string;
}

async function parseErrorBody(res: Response): Promise<AdminApiError> {
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string } };
    return {
      status: res.status,
      code: body.error?.code ?? 'UNKNOWN_ERROR',
      message: body.error?.message ?? 'אירעה שגיאה לא צפויה.',
    };
  } catch {
    return { status: res.status, code: 'UNKNOWN_ERROR', message: 'אירעה שגיאה לא צפויה.' };
  }
}

export interface QuoteListParams {
  page?: number;
  pageSize?: number;
  dateFrom?: string;
  dateTo?: string;
  q?: string;
}

export async function listQuotes(
  params: QuoteListParams = {},
): Promise<{ quotes: QuoteListItem[]; page: number; pageSize: number; total: number }> {
  const search = new URLSearchParams();
  if (params.page) search.set('page', String(params.page));
  if (params.pageSize) search.set('pageSize', String(params.pageSize));
  if (params.dateFrom) search.set('dateFrom', params.dateFrom);
  if (params.dateTo) search.set('dateTo', params.dateTo);
  if (params.q) search.set('q', params.q);

  const res = await fetch(`/api/admin/quotes?${search.toString()}`, { credentials: 'same-origin' });
  if (!res.ok) throw await parseErrorBody(res);
  return (await res.json()) as {
    quotes: QuoteListItem[];
    page: number;
    pageSize: number;
    total: number;
  };
}

export async function getQuoteStats(): Promise<{ todayCount: number; monthCount: number }> {
  const res = await fetch('/api/admin/quotes/stats', { credentials: 'same-origin' });
  if (!res.ok) throw await parseErrorBody(res);
  return (await res.json()) as { todayCount: number; monthCount: number };
}

export async function getQuote(id: number): Promise<{
  quote: QuoteDetail;
  items: QuoteItemDetail[];
  emailDeliveries: EmailDelivery[];
  whatsappDeliveries: WhatsappDelivery[];
}> {
  const res = await fetch(`/api/admin/quotes/${id}`, { credentials: 'same-origin' });
  if (!res.ok) throw await parseErrorBody(res);
  return (await res.json()) as {
    quote: QuoteDetail;
    items: QuoteItemDetail[];
    emailDeliveries: EmailDelivery[];
    whatsappDeliveries: WhatsappDelivery[];
  };
}

export function quotePdfUrl(id: number): string {
  return `/api/admin/quotes/${id}/pdf`;
}

export async function resendQuoteEmail(id: number): Promise<void> {
  const res = await fetch(`/api/admin/quotes/${id}/resend-email`, {
    method: 'POST',
    credentials: 'same-origin',
  });
  if (!res.ok) throw await parseErrorBody(res);
}

export async function sendQuoteWhatsappManual(
  id: number,
): Promise<{ clickToChatUrl: string; pdfUrl: string; pdfFilename: string }> {
  const res = await fetch(`/api/admin/quotes/${id}/whatsapp/manual-send`, {
    method: 'POST',
    credentials: 'same-origin',
  });
  if (!res.ok) throw await parseErrorBody(res);
  return (await res.json()) as { clickToChatUrl: string; pdfUrl: string; pdfFilename: string };
}

// --- הצעה ידנית (TASKS 5.5) -------------------------------------------------

export interface ManualQuoteMeasurements {
  qty: number;
  lengthM?: number;
}

export interface ManualQuoteItemInput {
  productId: number;
  variantId: number | null;
  measurements: ManualQuoteMeasurements;
  priceOverride?: number;
}

export interface PricedPreviewLine {
  productId: number;
  variantId: number | null;
  sku: string;
  nameSnapshot: string;
  qty: number;
  unitPrice: number;
  lineTotal: number;
  priceSource: PriceBasis;
  isEstimate: boolean;
  isPriceOnRequest: boolean;
}

export async function previewManualQuote(
  customerId: number | null,
  items: ManualQuoteItemInput[],
): Promise<{
  lines: PricedPreviewLine[];
  subtotal: number;
  vatRate: number;
  vatAmount: number;
  total: number;
}> {
  const res = await fetch('/api/admin/quotes/preview', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ customerId, items }),
  });
  if (!res.ok) throw await parseErrorBody(res);
  return (await res.json()) as {
    lines: PricedPreviewLine[];
    subtotal: number;
    vatRate: number;
    vatAmount: number;
    total: number;
  };
}

export async function createManualQuote(input: {
  customerId: number;
  items: ManualQuoteItemInput[];
  saveAsFixedPrices: boolean;
}): Promise<{ quoteId: number; quoteNumber: string }> {
  const res = await fetch('/api/admin/quotes', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await parseErrorBody(res);
  return (await res.json()) as { quoteId: number; quoteNumber: string };
}
